import { getCurrencyFormatter } from "@redotech/money/util";
import {
  BASE_BUSINESS_DAYS_REGEX,
  BASE_DYNAMIC_WEEKDAY_REGEX,
  BUSINESS_DAYS_VARIABLE,
  CARRIER_VARIABLE,
  CUSTOMER_NAME_VARIABLE,
  DELIVERY_DATE_VARIABLE,
  DYNAMIC_WEEKDAY_VARIABLE,
  LCG_DUTIES_AND_FEES_VARIABLE,
  LCG_DUTIES_VARIABLE,
  LCG_FEES_VARIABLE,
  LCG_SHIPPING_VARIABLE,
  LCG_TAXES_VARIABLE,
  SERVICE_VARIABLE,
  formatRateTableDisplayText,
  interpolateLandedCostVariables,
  textContainsCustomerNameVariable,
  textContainsLandedCostVariable,
} from "@redotech/redo-model/conversion/delivery-date/format-rate-table-display-text";
import {
  Carriers,
  UspsServices,
} from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import {
  DisplayedDynamicShippingRateTable,
  RateTableType,
} from "@redotech/redo-model/integration/redo-in-shipping/shipping-rate-table";
import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { RateTableTextFormatUi } from "./advanced-text-format-options";

export function markupText({
  rateTable,
  useConstraintsV2,
}: {
  rateTable: DisplayedDynamicShippingRateTable;
  useConstraintsV2: boolean;
}) {
  if (
    useConstraintsV2 ||
    !isDef(rateTable.markup) ||
    (!isDef(rateTable.markup.percentage) && !isDef(rateTable.markup.amount))
  ) {
    return "Dynamic";
  }

  let markupText = "";
  if (rateTable.markup.percentage && rateTable.markup.amount) {
    const markupAmount = +rateTable.markup.amount.value;
    const sign = markupAmount > 0 ? "+" : "-";
    markupText = `${rateTable.markup.percentage}% ${sign} ${getCurrencyFormatter(
      rateTable.markup.amount.currency,
      "symbol",
    ).format(Math.abs(markupAmount))}`;
  } else if (rateTable.markup.percentage) {
    markupText = `${rateTable.markup.percentage}%`;
  } else if (rateTable.markup.amount) {
    markupText = `${getCurrencyFormatter(
      rateTable.markup.amount.currency,
      "symbol",
    ).format(+rateTable.markup.amount.value)}`;
  }

  markupText += " markup";

  return markupText;
}

export function getRateTableTextDescriptionOrPreview({
  rateTableType,
  text,
  advancedTextFormatOptions,
  addLandedCosts,
}: {
  rateTableType: RateTableType;
  text: string;
  advancedTextFormatOptions: RateTableTextFormatUi;
  addLandedCosts?: boolean;
}) {
  switch (rateTableType) {
    case RateTableType.FIXED:
      return undefined;
    case RateTableType.DYNAMIC: {
      const hasDeliveryDateVariable =
        text.includes(DELIVERY_DATE_VARIABLE) ||
        BASE_DYNAMIC_WEEKDAY_REGEX.test(text) ||
        BASE_BUSINESS_DAYS_REGEX.test(text);
      const hasCustomerNameVariable = textContainsCustomerNameVariable(text);
      const hasLandedCostVariable =
        addLandedCosts && textContainsLandedCostVariable(text);
      const hasCarrierOrServiceVariable =
        text.includes(CARRIER_VARIABLE) || text.includes(SERVICE_VARIABLE);
      const landedCostHint = addLandedCosts
        ? `, ${LCG_DUTIES_VARIABLE}, ${LCG_TAXES_VARIABLE}, ${LCG_FEES_VARIABLE}, ${LCG_SHIPPING_VARIABLE}, ${LCG_DUTIES_AND_FEES_VARIABLE}`
        : "";
      const variableHint = `Use ${DELIVERY_DATE_VARIABLE}, ${DYNAMIC_WEEKDAY_VARIABLE}, ${BUSINESS_DAYS_VARIABLE}, ${CUSTOMER_NAME_VARIABLE}, ${CARRIER_VARIABLE}, ${SERVICE_VARIABLE}${landedCostHint} to insert dynamic info.`;
      if (
        hasDeliveryDateVariable ||
        hasCustomerNameVariable ||
        hasLandedCostVariable ||
        hasCarrierOrServiceVariable
      ) {
        const now = Temporal.Now.zonedDateTimeISO();
        const deliveryDate = now.add({ days: 5 });
        let previewText = formatRateTableDisplayText({
          textTemplate: text,
          deliveryDate,
          now,
          locale: "en-US",
          rateTableTextFormat: {
            deliveryDateFormat:
              advancedTextFormatOptions.deliveryDateFormatEnabled
                ? advancedTextFormatOptions.deliveryDateFormat
                : undefined,
            range: advancedTextFormatOptions.rangeEnabled
              ? advancedTextFormatOptions.range
              : undefined,
          },
          // chosen arbitrarily since it doesn't matter much for the UI preview
          carrier: Carriers.USPS,
          service: UspsServices.GroundAdvantage,
          customer: { name: { first: "Jane", last: "Doe" } },
        });
        if (hasLandedCostVariable) {
          previewText = interpolateLandedCostVariables({
            text: previewText,
            landedCostFormatted: {
              duties: "$4.50",
              taxes: "$2.30",
              fees: "$1.20",
              shipping: "$38.30",
              dutiesAndFees: "$5.70",
            },
          });
        }
        return `Preview: "${previewText}"\n${variableHint}`;
      }
      return variableHint;
    }
    default:
      assertNever(rateTableType);
  }
}

export function getRateTableTextInfoTooltip({
  addLandedCosts,
}: { addLandedCosts?: boolean } = {}): string {
  let tooltip = `Use ${DELIVERY_DATE_VARIABLE} to show a dynamic delivery date. Use ${DYNAMIC_WEEKDAY_VARIABLE} to show a dynamic weekday. Use ${BUSINESS_DAYS_VARIABLE} to show business days until delivery. Use ${CUSTOMER_NAME_VARIABLE} to show the customer's name. Use ${CARRIER_VARIABLE} to show the carrier name. Use ${SERVICE_VARIABLE} to show the carrier service name.`;
  if (addLandedCosts) {
    tooltip += ` Use ${LCG_DUTIES_VARIABLE} for duties, ${LCG_TAXES_VARIABLE} for taxes, or ${LCG_FEES_VARIABLE} for fees from landed cost calculations.`;
  }
  return tooltip;
}

export const rateTableTextInfoTooltip = getRateTableTextInfoTooltip();

export const preventWheelDefault: React.WheelEventHandler<HTMLInputElement> = (
  e,
) => {
  e.currentTarget.blur();
};
